import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from "../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../foundation/altzero_base/base/components/ui/tabs";
import {
  Building2,
  ArrowLeft,
  Star,
  Calendar,
  Leaf,
  DollarSign,
  Zap,
  AlertCircle,
  Store,
  MapPin
} from "lucide-react";
import { formatTermLabel } from '../lib/termLabel';
import { formatEnergyRate } from '../lib/ratePrecision';

interface TenderSite {
  id: string;
  name: string;
  nmi: string;
  addr_street?: string;
  addr_city?: string;
  addr_state?: string;
  addr_postcode?: string;
  addr_country?: string;
  annual_kwh_peak?: number;
  annual_kwh_offpeak?: number;
  annual_kwh_shoulder?: number;
  abn?: string;
  acn?: string;
  due_at?: string;
  current_contract_expiry?: string;
  current_meter_provider?: string;
  current_retailer?: string;
  dma_provider?: string;
  retailer_comms?: string;
  create_dma?: boolean;
  request_meter_data?: boolean;
  request_bills?: boolean;
  acquire_ced?: boolean;
}

interface Offer {
  id: string;
  tender_site_id: string;
  retailer_id: string;
  retailer?: {
    id: string;
    name: string;
    logo?: string;
  };
  offer_type: string;
  available_terms: number[];
  green_percent: number;
  is_recommended: boolean;
  recommended_months: number[];
  multi_year_rate_data?: any;
  created_at: string;
}

interface SiteOffersViewProps {
  site: TenderSite;
  offers: Offer[];
  onBack: () => void;
}

// Extended offer with specific contract term
type OfferWithTerm = Offer & { selectedTerm: number };

const SiteOffersView: React.FC<SiteOffersViewProps> = ({
  site,
  offers,
  onBack
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'recommended'>('all');

  // Filter offers for this site
  const siteOffers = offers.filter(offer => offer.tender_site_id === site.id);
  const recommendedOffers = siteOffers.filter(offer =>
    offer.recommended_months && offer.recommended_months.length > 0
  );

  // Expand offers by their contract terms
  const expandedAllOffers: OfferWithTerm[] = [];
  siteOffers.forEach(offer => {
    const terms = offer.available_terms && offer.available_terms.length > 0
      ? offer.available_terms
      : [12]; // Default fallback
    terms.forEach(term => {
      expandedAllOffers.push({ ...offer, selectedTerm: term });
    });
  });

  // Expand recommended offers by their recommended_months
  const expandedRecommendedOffers: OfferWithTerm[] = [];
  recommendedOffers.forEach(offer => {
    const terms = offer.recommended_months && offer.recommended_months.length > 0
      ? offer.recommended_months
      : offer.available_terms || [12];
    terms.forEach(term => {
      expandedRecommendedOffers.push({ ...offer, selectedTerm: term });
    });
  });

  // Helper function to format contract term (actual term length)
  const formatContractTerm = (offer: Offer): string => {
    if (!offer.available_terms || offer.available_terms.length === 0) {
      return 'N/A';
    }
    if (offer.available_terms.length === 1) {
      return formatTermLabel(offer.available_terms[0]);
    }
    return offer.available_terms.map((t: number) => formatTermLabel(t)).join(', ');
  };

  // Helper function to format recommended months (for recommended terms display)
  const formatRecommendedMonths = (offer: Offer): string => {
    if (!offer.recommended_months || offer.recommended_months.length === 0) {
      return "No recommendations";
    }

    if (offer.recommended_months.length === 1) {
      return formatTermLabel(offer.recommended_months[0]);
    }

    const sortedMonths = [...offer.recommended_months].sort((a, b) => a - b);
    return sortedMonths.map((m: number) => formatTermLabel(m)).join(', ');
  };

  // Helper function to format address
  const formatAddress = (site: TenderSite): string => {
    const parts = [
      site.addr_street,
      site.addr_city,
      site.addr_state
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Address not specified';
  };

  // Render individual offer card with specific contract term
  const renderOfferCard = (offerWithTerm: OfferWithTerm) => {
    const offer = offerWithTerm;
    const term = offerWithTerm.selectedTerm;
    const retailer = offer.retailer;
    const maxYear = Math.ceil(term / 12);

    // Extract rate data for this specific contract term
    const contractTermData = offer.multi_year_rate_data?.contract_terms?.[term.toString()];

    return (
      <Card key={`${offer.id}-${term}`} className="border border-gray-200 hover:shadow-md transition-shadow">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {/* Retailer Logo */}
              <div className="flex-shrink-0">
                {retailer?.logo ? (
                  <img
                    src={retailer.logo}
                    alt={`${retailer.name} logo`}
                    className="h-12 w-12 object-contain rounded border"
                  />
                ) : (
                  <div className="h-12 w-12 bg-gray-100 rounded border flex items-center justify-center">
                    <Store className="h-6 w-6 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Retailer Info */}
              <div>
                <CardTitle className="text-base font-semibold text-gray-900">
                  {retailer?.name || 'Unknown Retailer'}
                </CardTitle>
                <p className="text-sm text-gray-600">
                  {formatTermLabel(term)} contract
                </p>
              </div>
            </div>

            {/* Recommended Badge */}
            {offer.is_recommended && (
              <Badge className="bg-green-100 text-green-800 border-green-200">
                <Star className="w-3 h-3 mr-1" />
                Recommended
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Offer Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Contract Term */}
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <Calendar className="w-4 h-4 text-blue-600 mx-auto mb-1" />
              <div className="text-sm font-semibold text-blue-900">
                {formatTermLabel(term)}
              </div>
              <div className="text-xs text-gray-600">Contract Length</div>
            </div>

            {/* Green Energy */}
            {offer.green_percent > 0 && (
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <Leaf className="w-4 h-4 text-green-600 mx-auto mb-1" />
                <div className="text-sm font-semibold text-green-900">
                  {offer.green_percent}%
                </div>
                <div className="text-xs text-gray-600">Green Energy</div>
              </div>
            )}

            {/* Offer Type */}
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <Zap className="w-4 h-4 text-purple-600 mx-auto mb-1" />
              <div className="text-sm font-semibold text-purple-900">
                {offer.offer_type === 'electricity' ? 'Electricity' : 'Gas'}
              </div>
              <div className="text-xs text-gray-600">Service Type</div>
            </div>

            {/* Created Date */}
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <Calendar className="w-4 h-4 text-gray-600 mx-auto mb-1" />
              <div className="text-sm font-semibold text-gray-900">
                {new Date(offer.created_at).toLocaleDateString()}
              </div>
              <div className="text-xs text-gray-600">Created</div>
            </div>
          </div>

          {/* Recommended Months Display */}
          {offer.recommended_months && offer.recommended_months.length > 0 && (
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">
                  Recommended Contract Lengths
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {offer.recommended_months.map((months) => (
                  <Badge key={months} variant="outline" className="text-xs bg-yellow-100 text-yellow-800 border-yellow-300">
                    {formatTermLabel(months)}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Rate Data for this Contract Term */}
          {contractTermData && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-blue-600" />
                  Energy Rates ({formatTermLabel(term)} contract)
                </h4>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Peak Rates */}
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Peak Rates</div>
                      {Object.entries(contractTermData)
                        .filter(([key]) => key.startsWith('year_'))
                        .sort((a, b) => parseInt(a[0].split('_')[1]) - parseInt(b[0].split('_')[1]))
                        .map(([year, data]: [string, any]) => {
                          const yearNum = parseInt(year.split('_')[1]);
                          if (yearNum > maxYear) return null;
                          const rate = data?.peak;
                          return rate ? (
                            <div key={year} className="flex justify-between text-sm">
                              <span className="text-gray-700">Year {yearNum}</span>
                              <span className="font-medium">{formatEnergyRate(Number(rate))}c/kWh</span>
                            </div>
                          ) : null;
                        })
                        .filter(Boolean)}
                    </div>

                    {/* Off-Peak Rates */}
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Off-Peak Rates</div>
                      {Object.entries(contractTermData)
                        .filter(([key]) => key.startsWith('year_'))
                        .sort((a, b) => parseInt(a[0].split('_')[1]) - parseInt(b[0].split('_')[1]))
                        .map(([year, data]: [string, any]) => {
                          const yearNum = parseInt(year.split('_')[1]);
                          if (yearNum > maxYear) return null;
                          const rate = data?.offpeak;
                          return rate ? (
                            <div key={year} className="flex justify-between text-sm">
                              <span className="text-gray-700">Year {yearNum}</span>
                              <span className="font-medium">{formatEnergyRate(Number(rate))}c/kWh</span>
                            </div>
                          ) : null;
                        })
                        .filter(Boolean)}
                    </div>

                    {/* Shoulder Rates */}
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">Shoulder Rates</div>
                      {Object.entries(contractTermData)
                        .filter(([key]) => key.startsWith('year_'))
                        .sort((a, b) => parseInt(a[0].split('_')[1]) - parseInt(b[0].split('_')[1]))
                        .map(([year, data]: [string, any]) => {
                          const yearNum = parseInt(year.split('_')[1]);
                          if (yearNum > maxYear) return null;
                          const rate = data?.shoulder;
                          return rate ? (
                            <div key={year} className="flex justify-between text-sm">
                              <span className="text-gray-700">Year {yearNum}</span>
                              <span className="font-medium">{formatEnergyRate(Number(rate))}c/kWh</span>
                            </div>
                          ) : null;
                        })
                        .filter(Boolean)}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Contract Term Information */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">Contract Length</div>
            <div className="text-sm font-semibold text-gray-800">
              {formatTermLabel(term)}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 w-4" />
          Back to Sites
        </Button>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Offers for {site.name || 'Unnamed Site'}
          </h3>

          {/* Site Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3 p-4 bg-gray-50 rounded-lg">
            {/* Basic Info */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-gray-500" />
                <span className="font-medium text-gray-700">NMI:</span>
                <span className="text-gray-900">{site.nmi || 'N/A'}</span>
              </div>
              <div className="text-sm text-gray-600">
                {formatAddress(site)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offers Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'all' | 'recommended')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="all" className="flex items-center gap-2">
            All Offers
            <Badge variant="secondary" className="text-xs">
              {expandedAllOffers.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="recommended" className="flex items-center gap-2">
            Recommended
            <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
              {expandedRecommendedOffers.length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* All Offers Tab */}
        <TabsContent value="all" className="space-y-4">
          {expandedAllOffers.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  No Offers Found
                </h4>
                <p className="text-sm text-gray-600 text-center max-w-md">
                  This site doesn't have any offers configured yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {expandedAllOffers.map(renderOfferCard)}
            </div>
          )}
        </TabsContent>

        {/* Recommended Offers Tab */}
        <TabsContent value="recommended" className="space-y-4">
          {expandedRecommendedOffers.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Star className="w-12 h-12 text-gray-400 mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  No Recommended Offers
                </h4>
                <p className="text-sm text-gray-600 text-center max-w-md">
                  This site doesn't have any recommended offers yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {expandedRecommendedOffers.map(renderOfferCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SiteOffersView;
